import { useCallback, useMemo } from 'react';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { ClipDeletes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { DeleteType } from '@goldcast/player-config/src/types/asset-types';
import { Word } from '@goldcast/player-config/src/types/transcript-types';
import { formatDuration, getFillerWordsInfo, hasFillerWordsDeleted } from './utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';

export interface UseFillerWordsReturn {
  hasFillerWords: boolean;
  hasFillerWordsDeleted: boolean;
  fillerWordsCount: number;
  fillerWordsDuration: string;
  handleApplyDeletes: () => void;
  handleRevertDeletes: () => void;
  deleteAllFillerWords: (deleteType: DeleteType, clipData: Clip) => void;
}

export const useFillerWords = (
  onEditActionTriggered?: () => void,
  onCloseMenu?: () => void,
  setFillerWordsMenuOpen?: (open: boolean) => void
): UseFillerWordsReturn => {
  const { clipId, clipData } = useClipsContext();

  const { fillerWordsCount, fillerWordDuration } = useMemo(() => {
    return getFillerWordsInfo(clipData?.asset_metadata?.captions);
  }, [clipData?.asset_metadata?.captions]);

  const fillerWordsDuration = useMemo(() => {
    return formatDuration(fillerWordDuration);
  }, [fillerWordDuration]);

  const hasFillerWordsDeletedValue = useMemo(
    () => hasFillerWordsDeleted(clipData?.asset_metadata?.deletes),
    [clipData?.asset_metadata?.deletes]
  );

  const deleteAllFillerWords = useCallback(
    (deleteType: DeleteType, currentClipData: Clip) => {
      if (!currentClipData?.asset_metadata?.captions || !clipId) {
        return;
      }

      const allFillerWords: Word[] = currentClipData.asset_metadata.captions.filter(word => word.is_filler === true);

      if (allFillerWords.length === 0) {
        return;
      }

      // Group filler words into continuous segments based on index
      const deleteSegments: Word[][] = [];
      let currentDeleteSegment: Word[] = [];

      for (let i = 0; i < allFillerWords.length; i++) {
        const currentFillerWord = allFillerWords[i];

        if (currentDeleteSegment.length === 0) {
          currentDeleteSegment.push(currentFillerWord);
        } else {
          const lastFillerWordInSegment = currentDeleteSegment[currentDeleteSegment.length - 1];
          if (currentFillerWord.index === lastFillerWordInSegment.index + 1) {
            currentDeleteSegment.push(currentFillerWord);
          } else {
            // Start new segment
            deleteSegments.push([...currentDeleteSegment]);
            currentDeleteSegment = [currentFillerWord];
          }
        }
      }

      // Add the last segment
      if (currentDeleteSegment.length > 0) {
        deleteSegments.push(currentDeleteSegment);
      }

      // Create delete ranges for each continuous segment
      const fillerWordDeleteRanges: ClipDeletes = {};

      deleteSegments.forEach(deleteSegment => {
        const firstFillerWord = deleteSegment[0];
        const lastFillerWord = deleteSegment[deleteSegment.length - 1];

        const segmentStartTime = firstFillerWord.start_time;
        const segmentEndTime = lastFillerWord.end_time;

        fillerWordDeleteRanges[segmentStartTime] = {
          prev_end_time: segmentStartTime,
          next_start_time: segmentEndTime,
          bounds: [segmentStartTime, segmentEndTime],
          deleteType: deleteType
        };
      });

      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.BULK_ADD_COMPOSITE_SEGMENT_DELETES,
        deletes: fillerWordDeleteRanges
      });
    },
    [clipId]
  );

  const handleRevertDeletes = useCallback(() => {
    if (!clipId || !clipData?.asset_metadata?.deletes) {
      return;
    }

    if (onEditActionTriggered) {
      onEditActionTriggered();
    }

    const deletesToKeep: ClipDeletes = {};

    Object.entries(clipData.asset_metadata.deletes).forEach(([startTime, deleteBounds]) => {
      if (deleteBounds.deleteType !== 'FILLER_WORDS_MENU') {
        deletesToKeep[startTime] = deleteBounds;
      }
    });

    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.BULK_PUT_COMPOSITE_SEGMENT_DELETE,
      deletes: deletesToKeep
    });

    if (onCloseMenu) {
      onCloseMenu();
    }
    if (setFillerWordsMenuOpen) {
      setFillerWordsMenuOpen(false);
    }
  }, [clipId, clipData?.asset_metadata?.deletes, onEditActionTriggered, onCloseMenu, setFillerWordsMenuOpen]);

  const handleApplyDeletes = useCallback(() => {
    if (onEditActionTriggered) {
      onEditActionTriggered();
    }
    deleteAllFillerWords('FILLER_WORDS_MENU', clipData);
    if (onCloseMenu) {
      onCloseMenu();
    }
    if (setFillerWordsMenuOpen) {
      setFillerWordsMenuOpen(false);
    }
  }, [onEditActionTriggered, onCloseMenu, deleteAllFillerWords, clipData, setFillerWordsMenuOpen]);

  return {
    hasFillerWords: fillerWordsCount > 0,
    hasFillerWordsDeleted: hasFillerWordsDeletedValue,
    fillerWordsCount,
    fillerWordsDuration,
    handleApplyDeletes,
    handleRevertDeletes,
    deleteAllFillerWords
  };
};
